import React, { FC, useMemo, useCallback, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Checkbox,
  styled,
  Button,
  Box,
  PaperProps,
  PopperProps,
  Popper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Typography } from '../Typography/Typography';
import { SelectItem } from '../../types';

interface HierarchicalOption {
  parent: SelectItem;
  children: SelectItem[];
}

interface Props {
  options: HierarchicalOption[];
  selectedItems: SelectItem[];
  onSelect: (items: SelectItem[]) => void;
  placeholder?: string;
  subtitle?: string;
  width?: number | string;
  disablePortal?: boolean;
  className?: string;
}

type FlatChild = {
  id: string;
  label: string;
  groupId: string;
  groupLabel: string;
  raw: SelectItem;
  isStandalone?: boolean; 
};

const Container = styled('div')<{ $width?: number | string }>`
  width: ${({ $width }) => ($width != null ? `${$width}px` : '320px')};
  display: inline-block;
`;

const StyledPopper = styled((props: PopperProps) => <Popper {...props} />)`
  width: 320px !important;
`;

const ListHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 0 12px;
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${({ theme }) => theme.colors?.surfaceBackground?.bg1};
`;

const GroupHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px !important;
  margin: 2px 8px 2px 16px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors?.text?.secondary};
  user-select: none;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors?.surfaceInteraction?.selectedLight};
  }
`;


export const HierarchicalDropdownMultiSelect: FC<Props> = ({
  options,
  selectedItems,
  onSelect,
  placeholder = 'Select options',
  subtitle = 'All Users',
  width = 320,
  disablePortal = true,
  className,
}) => {
  const { colors } = useTheme();
  const listboxRef = useRef<HTMLUListElement | null>(null);

  const { flatChildren, idToChild, groupToChildren } = useMemo(() => {
    const flat: FlatChild[] = [];
    const idToChild = new Map<string, FlatChild>();
    const groupToChildren = new Map<string, FlatChild[]>();

    for (const group of options) {
      const groupId = group.parent.id.toString();
      const groupLabel = group.parent.value;

      if (group.children.length === 1 && !group.children[0].value.trim()) {
        const fo: FlatChild = {
          id: group.parent.id.toString(),
          label: group.parent.value,
          groupId: 'standalone', 
          groupLabel: '',
          raw: group.parent,
          isStandalone: true,
        };
        flat.push(fo);
        idToChild.set(fo.id, fo);
      } else {
        for (const ch of group.children) {
          if (ch.value.trim()) { 
            const fo: FlatChild = {
              id: ch.id.toString(),
              label: ch.value,
              groupId,
              groupLabel,
              raw: ch,
            };
            flat.push(fo);
            idToChild.set(fo.id, fo);
            if (!groupToChildren.get(groupId)) groupToChildren.set(groupId, []);
            groupToChildren.get(groupId)!.push(fo);
          }
        }
      }
    }

    return { flatChildren: flat, idToChild, groupToChildren };
  }, [options]);

  const selectedIds = useMemo(
    () => new Set(selectedItems.map((s) => s.id.toString())),
    [selectedItems]
  );

  const groupState = useMemo(() => {
    const map = new Map<string, 'none' | 'partial' | 'all'>();
    for (const [gid, children] of groupToChildren.entries()) {
      const total = children.length;
      const picked = children.filter((c) => selectedIds.has(c.id)).length;
      if (picked === 0) map.set(gid, 'none');
      else if (picked === total) map.set(gid, 'all');
      else map.set(gid, 'partial');
    }
    return map;
  }, [groupToChildren, selectedIds]);

  const valueFlat: FlatChild[] = useMemo(
    () => selectedItems.map((s) => idToChild.get(s.id.toString())).filter(Boolean) as FlatChild[],
    [selectedItems, idToChild]
  );

  const setSelectedByIds = useCallback(
    (ids: string[]) => {
      const prevTop = listboxRef.current?.scrollTop ?? 0;

      const next = ids
        .map((id) => idToChild.get(id))
        .filter(Boolean)
        .map((fo) => (fo as any).raw as SelectItem);

      onSelect(next);

      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = prevTop;
        }
      });
    },
    [onSelect, idToChild]
  );

  const toggleChild = useCallback(
    (child: FlatChild) => {
      const isSelected = selectedIds.has(child.id);
      if (isSelected) {
        setSelectedByIds([...selectedIds].filter((id) => id !== child.id));
      } else {
        setSelectedByIds([...selectedIds, child.id]);
      }
    },
    [selectedIds, setSelectedByIds]
  );

  const toggleGroupAll = useCallback(
    (groupId: string) => {
      const children = groupToChildren.get(groupId) ?? [];
      const state = groupState.get(groupId) ?? 'none';

      if (state === 'all') {
        const remaining = [...selectedIds].filter((id) => !children.some((c) => c.id === id));
        setSelectedByIds(remaining);
      } else {
        const childIds = children.map((c) => c.id);
        const merged = new Set([...selectedIds, ...childIds]);
        setSelectedByIds([...merged]);
      }
    },
    [groupToChildren, groupState, selectedIds, setSelectedByIds]
  );

  const filterOptions = useMemo(
    () =>
      createFilterOptions<FlatChild>({
        stringify: (opt) => `${opt.label} ${opt.groupLabel}`.toLowerCase(),
        trim: true,
      }),
    []
  );

  const renderTags = useCallback(
    (tagValue: FlatChild[]) => {
      if (tagValue.length === 0) {
        return (
          <Typography variant='body' color={colors.text.caption}>
            {placeholder}
          </Typography>
        );
      }

      const label = tagValue.length === 1 ? tagValue[0].label : `${tagValue.length} selected`;

      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 28,
            lineHeight: '28px',
            maxWidth: 'calc(100% - 64px)',
            pl: 0.5,
            minWidth: 0,
          }}
          title={label}
        >
          <Typography
            variant='body'
            color={colors.text.main}
            style={{
              lineHeight: 'inherit',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
        </Box>
      );
    },
    [colors.text.caption, colors.text.main, placeholder]
  );

  const PaperComponent: PaperProps['component'] = useCallback(
    (props: any) => {
      const { children, ...rest } = props;
      return (
        <Box
          {...rest}
          sx={{
            border: `1px solid ${colors.border?.default}`,
            backgroundColor: colors.surfaceBackground?.bg1,
            borderRadius: '4px',
            position: 'relative',
            left: '50px',
          }}
        >
          <ListHeader>
            <Typography variant='subtitle1' color={colors.text?.secondary}>
              {subtitle}
            </Typography>
            <Button
              variant='text'
              size='small'
              disabled={selectedIds.size === 0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect([])}
              sx={{
                color: colors.textAccent.default,
                '&.Mui-disabled': {
                  opacity: 1,
                  pointerEvents: 'auto',
                  cursor: 'default',
                  color: colors.textAccent.disabled
                },
              }}
            >
              Clear All
            </Button>
          </ListHeader>
          {children}
        </Box>
      );
    },
    [colors, subtitle, selectedIds.size, onSelect]
  );

  return (
    <Container $width={width} className={className}>
      <Autocomplete
        multiple
        clearIcon={null}
        disableCloseOnSelect
        disablePortal={disablePortal}
        autoHighlight={false}
        PopperComponent={StyledPopper}
        options={flatChildren}
        value={valueFlat}
        filterOptions={filterOptions}
        groupBy={(o: any) => o.isStandalone ? null : o.groupLabel}
        getOptionLabel={(o: any) => o.label}
        isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
        ListboxProps={{
          ref: listboxRef as any,
          style: { maxHeight: 300, overflow: 'auto' },
        }}
        renderInput={(params) => {
          const hasSelection = valueFlat.length > 0;

          return (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                placeholder: hasSelection ? '' : placeholder,
              }}
              variant='outlined'
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '8px',
                  paddingTop: 0,
                  paddingBottom: 0,
                  width: '220px',
                  minHeight: '36px',
                  backgroundColor: colors.surfaceBackground?.bg1,
                  color: colors.text?.main,
                  borderColor: colors.border?.default,
                },
                '& .MuiAutocomplete-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: '0 0 0 4px',
                  lineHeight: '28px',
                },
                '& .MuiInputBase-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiAutocomplete-endAdornment': {
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
              }}
            />
          );
        }}
        renderGroup={({ key, group, children }) => {
          if (!group) {
            return <React.Fragment key={key}>{children}</React.Fragment>;
          }

          const firstChild: any = children?.[0];
          const optionObj: FlatChild | undefined = firstChild?.props?.option as
            | FlatChild
            | undefined;

          const groupId =
            optionObj?.groupId ??
            [...groupToChildren.entries()].find(([, arr]) => arr[0]?.groupLabel === group)?.[0];

          const state = groupId ? groupState.get(groupId) ?? 'none' : 'none';

          return (
            <Box key={key} sx={{ borderTop: `1px solid ${colors.border?.default}` }}>
              <GroupHeader
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (groupId) toggleGroupAll(groupId);
                }}
              >
                <Checkbox
                  size='small'
                  checked={state === 'all'}
                  indeterminate={state === 'partial'}
                  sx={{ padding: '0 4px 0 0' }}
                />
                <Typography variant='body' color={colors.text.secondary}>
                  {group}
                </Typography>
              </GroupHeader>
              {children}
            </Box>
          );
        }}
        renderOption={(liProps, option) => {
          const checked = selectedIds.has(option.id);
          const StyledOptionRow = styled('li')<{ $isStandalone?: boolean }>`
            display: flex;
            align-items: center;
            padding: 2px 4px !important;
            margin: ${({ $isStandalone }) => $isStandalone ? '4px 8px 4px 16px' : '4px 8px 4px 40px'};
            border-radius: 4px;
            &.Mui-focused {
              background-color: ${({ theme }) => theme.colors?.surfaceInteraction?.selectedLight} !important;
            }
            &:hover {
              background-color: ${({ theme }) => theme.colors?.surfaceInteraction?.selectedLight} !important;
            }
            &[aria-selected='true'] {
              background-color: ${({ theme }) => theme.colors?.surfaceInteraction?.selectedLight} !important;
            }
          `;

          return (
            <StyledOptionRow
              {...liProps}
              key={option.id}
              $isStandalone={option.isStandalone}
              onClick={(e) => {
                e.stopPropagation();
                toggleChild(option);
              }}
            >
              <Checkbox size='small' checked={checked} sx={{ padding: '4px 8px 4px 0' }} />
              <Typography
                variant='body'
                color={colors.text.secondary}
                style={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {option.label}
              </Typography>
            </StyledOptionRow>
          );
        }}
        PaperComponent={PaperComponent as any}
        renderTags={renderTags as any}
        sx={{
          "& button[title='Clear'], & button[title='Open'], & button[title='Close']": {
            visibility: 'visible',
            color: colors.icon?.default,
          },
        }}
        onChange={(_, newFlatValue) => {
          const ids = (newFlatValue as FlatChild[]).map((o) => o.id);
          setSelectedByIds(ids);
        }}
      />
    </Container>
  );
};
